import { Text } from "components/general/Text";
import "../index.css";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { NAV_LINKS } from "config/consts";
import { showIntercom } from "hooks/useToggleIntercom";
import { Link } from "react-router-dom";
import { ReactComponent as BebopLogoIcon } from "../../../img/about/logo.svg";
import mixBytesLogoSrc from "../../../img/home/mixbytes.png";
import pessimisticLogoSrc from "../../../img/home/pessimistic.png";
import decurityLogoSrc from "../../../img/home/decurity.png";
import wintermuteNoTextLogo from "../../../img/home/wintermuteNoTextLogo.svg";
import wintermuteTextLogo from "../../../img/home/wintermuteTextLogo.svg";
import { MOBILE_WIDTH, TABLET_WIDTH } from "hooks/useDeviceType";
import { useEffect, useState } from "react";
import { AccordionDownIcon } from "components/TradeDetailsSection/elements/AccordionDownIcon";
import { AnimateChangeInHeight } from "components/general/AnimateChangeInHeight";
import { Spacer } from "components/general/Spacer/Spacer";
import { useMediaQuery } from "usehooks-ts";
import { GlobalStore } from "store/global/GlobalStore";

export default function Footer() {
  const isDesktopMode = useMediaQuery(`(min-width: ${TABLET_WIDTH + 50}px)`);
  return (
    <Container>
      <MainContainer>
        <LinksContainer>
          {FOOTER_LINKS.map(entry => (
            <LinkCategory key={entry.title} {...entry} />
          ))}
        </LinksContainer>
        <LogosContainer>
          <AuditedBy />
          <BackedBy />
          <BebopLogoContainer>
            <BebopLogoIcon width={isDesktopMode ? 251 : 146} />
          </BebopLogoContainer>
        </LogosContainer>
      </MainContainer>
      <DisclaimerContainer>
        <Text textStyle="Paragraph" size="XL">
          {DISCLAIMER_TEXT}
        </Text>
      </DisclaimerContainer>
      <Spacer height={200} />
    </Container>
  );
}

// Sub-components

const Container = styled.div();

const MainContainer = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 96,
  padding: "64px 63px 56px 63px",
  margin: "0 72px 70px 72px",
  borderRadius: 36,
  backgroundColor: theme.button.secondary.surface.default,

  [`@media (max-width: ${MOBILE_WIDTH}px)`]: {
    margin: "0 18px 40px",
    padding: "32px 24px",
  },
}));

const LinksContainer = styled.div(() => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  width: "100%",

  [`@media (max-width: ${TABLET_WIDTH}px)`]: {
    gridTemplateColumns: "1fr",
    gap: 24,
  },
}));

const LogosContainer = styled.div(({ theme }) => ({
  display: "grid",
  gridTemplateRows: "1fr 1fr",
  gridTemplateColumns: "repeat(5, 1fr)",
  color: theme.button.secondary.text.default,

  [`@media (max-width: ${TABLET_WIDTH}px)`]: {
    gridTemplateRows: "repeat(3, auto)",
    gridTemplateColumns: "1fr",
  },
}));

const logoContainerTextCss = css`
  margin-right: 14px;
  white-space: nowrap;
  @media (max-width: ${TABLET_WIDTH}px) {
    margin-right: 0;
    margin-bottom: 14px;
  }
`;

const AuditedBy = () => {
  const isDesktopMode = useMediaQuery(`(min-width: ${TABLET_WIDTH + 1}px)`);
  return (
    <AuditedByContainer>
      <Text textStyle="Title" size="Small" textCss={logoContainerTextCss}>
        Audited by
      </Text>
      <AuditedByImages>
        <Link
          to={NAV_LINKS.AUDIT_REPORT_MIXBYTES}
          target="_blank"
          rel="noreferrer nofollow"
          style={{ position: "relative", top: 2 }}
        >
          <img src={mixBytesLogoSrc} alt="MixBytes logo" height={22} />
        </Link>

        <Link
          to={NAV_LINKS.AUDIT_REPORT_PESSIMISTIC}
          target="_blank"
          rel="noreferrer nofollow"
          style={isDesktopMode ? { position: "relative", top: 4 } : undefined}
        >
          <img src={pessimisticLogoSrc} alt="Pessimistic logo" height={16} />
        </Link>

        <Link
          to={NAV_LINKS.AUDIT_REPORT_DECURITY}
          target="_blank"
          rel="noreferrer nofollow"
          style={{ position: "relative", top: -2 }}
        >
          <img src={decurityLogoSrc} alt="Decurity logo" height={26} />
        </Link>
      </AuditedByImages>
    </AuditedByContainer>
  );
};

const AuditedByImages = styled.div(() => ({
  display: "flex",
  gap: 26,
  [`@media (max-width: ${TABLET_WIDTH}px)`]: {
    flexDirection: "column",
  },
}));

const AuditedByContainer = styled.div(() => ({
  display: "flex",
  alignItems: "center",
  gridRowStart: 1,
  gridColumnStart: 1,
  gridRowEnd: 2,
  gridColumnEnd: 5,

  [`@media (max-width: ${TABLET_WIDTH}px)`]: {
    gridRowStart: 1,
    gridColumnStart: 1,
    gridRowEnd: 2,
    gridColumnEnd: 2,
    flexDirection: "column",
  },
}));

const BackedBy = () => {
  return (
    <BackedByContainer>
      <Text textStyle="Title" size="Small" textCss={logoContainerTextCss}>
        Backed by
      </Text>
      <Link
        to={NAV_LINKS.WINTERMUTE}
        target="_blank"
        rel="noreferrer nofollow"
        style={{ position: "relative" }}
      >
        <img
          src={wintermuteNoTextLogo}
          alt="Wintermute logo"
          height={23}
          style={{ position: "relative", top: 0, marginRight: 5 }}
        />
        <img
          src={wintermuteTextLogo}
          alt="Decurity logo"
          height={11}
          style={{ position: "relative", top: -4 }}
        />
      </Link>
    </BackedByContainer>
  );
};

const BackedByContainer = styled.div(() => ({
  display: "flex",
  alignItems: "center",
  gridRowStart: 2,
  gridColumnStart: 1,
  gridRowEnd: 3,
  gridColumnEnd: 5,

  [`@media (max-width: ${TABLET_WIDTH}px)`]: {
    gridRowStart: 2,
    gridColumnStart: 1,
    gridRowEnd: 3,
    gridColumnEnd: 2,
    flexDirection: "column",
    marginTop: 32,
  },
}));

const BebopLogoContainer = styled.div(() => ({
  gridRowStart: 1,
  gridColumnStart: 5,
  gridRowEnd: 3,
  gridColumnEnd: 6,
  margin: "auto",

  [`@media (max-width: ${TABLET_WIDTH}px)`]: {
    gridRowStart: 3,
    gridColumnStart: 1,
    gridRowEnd: 4,
    gridColumnEnd: 2,
    marginTop: 48,
  },
}));

const DisclaimerContainer = styled.div(() => ({
  padding: "0 135px",
  [`@media (max-width: ${MOBILE_WIDTH}px)`]: {
    padding: "0 100px",
  },
}));

const LinkCategory = ({ title, links }: FooterCategory) => {
  const theme = GlobalStore.useState(s => s.theme);
  const isDesktopMode = useMediaQuery(`(min-width: ${TABLET_WIDTH + 1}px)`);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isDesktopMode) {
      setIsOpen(false);
    }
  }, [isDesktopMode]);

  const titleContent = (
    <Text
      shouldScale={true}
      textStyle="Title"
      size="Large"
      textCss={css`
        margin-bottom: 14px;
      `}
    >
      {title}
    </Text>
  );
  const linkContent = (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {links.map(entry => {
        if ("to" in entry) {
          return (
            <Link key={entry.name} to={entry.to} target="_blank" rel="noreferrer nofollow">
              <Text
                textStyle="Paragraph"
                size="XL"
                textCss={css`
                  transition: color 0.2s linear;
                  &:hover {
                    color: ${theme["Brand colours"].Green};
                  }
                `}
              >
                {entry.name}
              </Text>
            </Link>
          );
        }
        return (
          <button key={entry.name} onClick={entry.onClick}>
            <Text
              textStyle="Paragraph"
              size="XL"
              textCss={css`
                transition: color 0.2s linear;
                &:hover {
                  color: ${theme["Brand colours"].Green};
                }
              `}
            >
              {entry.name}
            </Text>
          </button>
        );
      })}
    </div>
  );

  if (!isDesktopMode) {
    return (
      <AnimateChangeInHeight>
        <LinkCategoryContainer>
          <button
            onClick={() => setIsOpen(s => !s)}
            style={{ display: "flex", justifyContent: "space-between", width: "100%" }}
          >
            {titleContent}{" "}
            <AccordionDownIcon
              isAccordionOpen={isOpen}
              color={"inherit"}
              strokeWidth={2}
              css={css`
                position: relative;
              `}
            />
          </button>
          {isOpen && (
            <>
              <Spacer minHeight={12} />
              {linkContent}
            </>
          )}
        </LinkCategoryContainer>
      </AnimateChangeInHeight>
    );
  }

  return (
    <LinkCategoryContainer>
      {titleContent}
      {linkContent}
    </LinkCategoryContainer>
  );
};

const LinkCategoryContainer = styled.div(({ theme }) => ({
  color: theme.button.secondary.text.default,
}));

// Helpers

type BaseLink = { name: string };
interface LinkWithTo extends BaseLink {
  to: string;
}
interface LinkWithOnClick extends BaseLink {
  onClick: () => void;
}

interface FooterCategory {
  title: string;
  links: Array<LinkWithTo | LinkWithOnClick>;
}

const FOOTER_LINKS: Array<FooterCategory> = [
  {
    title: "General",
    links: [
      { name: "What is Bebop?", to: NAV_LINKS.DOCUMENTATION_BEBOP_INTRODUCTION },
      { name: "Trade", to: "/trade" },
      { name: "Activity", to: "/activity" },
      { name: "FAQs", to: NAV_LINKS.FAQ },
      { name: "Support", onClick: showIntercom },
      { name: "Join Us", to: NAV_LINKS.JOBS },
    ],
  },
  {
    title: "Developers",
    links: [
      { name: "API Docs", to: NAV_LINKS.DOCUMENTATION },
      { name: "FAQs", to: NAV_LINKS.FAQ_DEVELOPERS },
      { name: "Support", to: NAV_LINKS.EMAIL_DEVELOPERS },
    ],
  },
  {
    title: "Partners",
    links: [
      { name: "API Integrations", to: NAV_LINKS.FORM_API_INTEGRATION },
      { name: "Solvers", to: NAV_LINKS.FORM_SOLVERS },
      { name: "Market Makers", to: NAV_LINKS.FORM_MARKET_MAKERS },
      { name: "Reach Out", to: NAV_LINKS.FORM_REACH_OUT },
      { name: "Media Kit", to: NAV_LINKS.BRAND },
    ],
  },
  {
    title: "Legal",
    links: [
      { name: "Terms & Conditions", to: NAV_LINKS.TERMS_AND_CONDITIONS },
      { name: "Privacy Policy", to: NAV_LINKS.PRIVACY_POLICY },
      { name: "Disclaimer", to: "disclaimer.pdf" },
    ],
  },
];

const DISCLAIMER_TEXT =
  "Disclaimer: This Website is provided as a tool for users to interact with Bebop on their own initiative, and does not constitute an offer or commitment, a solicitation of an offer or commitment, or any advice or recommendation to enter into any cryptocurrency trading activity.  The protections provided by the regulatory systems in any jurisdiction will not be available to you and you acknowledge that you shall use Bebop’s APIs and Interface at your own risk.";
